
import { Component, Vue, Watch } from "vue-property-decorator";
import httpClient from "@/store/httpClient";
const Chart = require("chart.js");

// Priority order for color: highest index wins when multiple are active.
const TECHS = [
	{ field: "needsBend", label: "Abk.", color: "#1565C0" },
	{ field: "needsFeatures", label: "Umf.", color: "#468641" },
	{ field: "needsPressJoin", label: "Press.", color: "#E65100" },
	{ field: "needsWelding", label: "Schw.", color: "#B71C1C" },
	{ field: "needsPowderCoating", label: "Pulver", color: "#7B1FA2" },
	{ field: "needsHotDipCoating", label: "Feuerverz.", color: "#546E7A" },
	{ field: "needsGavanizeCoating", label: "Galv.", color: "#00796B" },
];

@Component
export default class OrderVolumePerTechnologyReport extends Vue {
	private chart: any = null;
	private orderVolume: any = [];
	private loading: boolean = false;
	private year: number = new Date().getFullYear();
	private detailView: boolean = false;

	private mounted() {
		this.loadReport();
	}

	private get years() {
		var lastYears = [];
		for (let index = new Date().getFullYear(); index >= 2012; index--) {
			lastYears.push(index);
		}
		return lastYears;
	}

	@Watch("year")
	private async loadReport() {
		try {
			this.loading = true;
			const response = await httpClient().get(
				`Report/GetOrderVolumePerTechnology?year=${this.year}`
			);
			this.orderVolume = response.data;
			this.updateDiagram();
		} catch (err) {
			console.error("Loading report failed.");
			console.error(err);
			this.$store.commit(
				"setSnackbarErrorText",
				"Fehler beim Laden der Reports. Siehe Konsole."
			);
		} finally {
			this.loading = false;
		}
	}

	@Watch("detailView")
	private onDetailViewChanged() {
		this.updateDiagram();
	}

	private sumTotal(orders: any[]): number {
		return Math.round(
			orders.map((o: any) => o.total).reduce((acc: number, v: number) => acc + v, 0)
		);
	}

	// ── Übersichtsdiagramm (8 Balken) ─────────────────────────────────────────

	private getSummaryDatasets() {
		const colors = [
			"#37474F", // Nur Laser
			"#1565C0", // Abkanten
			"#468641", // Umformen
			"#E65100", // Einpressungen
			"#B71C1C", // Schweissen
			"#7B1FA2", // Pulverbeschichten
			"#546E7A", // Feuerverzinken
			"#00796B", // Galvanisch Verzinken
		];
		const labels = [
			"Nur Lasergeschnitten",
			"Abkanten",
			"Umformen",
			"Einpressungen",
			"Schweissen",
			"Pulverbeschichten",
			"Feuerverzinken",
			"Galvanisch Verzinken",
		];
		const data = [
			this.sumTotal(
				this.orderVolume.filter(
					(o: any) =>
						!o.needsBend &&
						!o.needsFeatures &&
						!o.needsWelding &&
						!o.needsPowderCoating &&
						!o.needsPressJoin &&
						!o.needsHotDipCoating &&
						!o.needsGavanizeCoating
				)
			),
			this.sumTotal(this.orderVolume.filter((o: any) => o.needsBend)),
			this.sumTotal(this.orderVolume.filter((o: any) => o.needsFeatures)),
			this.sumTotal(this.orderVolume.filter((o: any) => o.needsPressJoin)),
			this.sumTotal(this.orderVolume.filter((o: any) => o.needsWelding)),
			this.sumTotal(this.orderVolume.filter((o: any) => o.needsPowderCoating)),
			this.sumTotal(this.orderVolume.filter((o: any) => o.needsHotDipCoating)),
			this.sumTotal(this.orderVolume.filter((o: any) => o.needsGavanizeCoating)),
		];
		return { labels, datasets: [{ backgroundColor: colors, data }] };
	}

	// ── Detaildiagramm (alle 128 Kombinationen, nur nicht-leere) ──────────────

	private getDetailDatasets() {
		const entries: { label: string; color: string; total: number }[] = [];

		for (let mask = 0; mask < 128; mask++) {
			const active = TECHS.filter((_, i) => (mask >> i) & 1);
			const total = this.sumTotal(
				this.orderVolume.filter((o: any) =>
					TECHS.every((t, i) => o[t.field] === !!((mask >> i) & 1))
				)
			);
			if (total === 0) continue;

			const label = active.length === 0 ? "Nur Laser" : active.map((t) => t.label).join(" +");

			// Farbe der höchstpriorisierten aktiven Technologie
			const color = active.length === 0 ? "#37474F" : active[active.length - 1].color;

			entries.push({ label, color, total });
		}

		// Absteigend nach Volumen sortieren
		entries.sort((a, b) => b.total - a.total);

		return {
			labels: entries.map((e) => e.label),
			datasets: [
				{
					backgroundColor: entries.map((e) => e.color),
					data: entries.map((e) => e.total),
				},
			],
		};
	}

	private updateDiagram() {
		if (this.chart != null) {
			this.chart.destroy();
		}

		const ctx = (this.$refs.diagramCanvas as any).getContext("2d");
		this.chart = new Chart(ctx, {
			type: "bar",
			data: this.detailView ? this.getDetailDatasets() : this.getSummaryDatasets(),
			options: {
				plugins: {
					legend: { display: false },
				},
				scales: {
					y: { beginAtZero: true },
				},
			},
		});
		this.chart.update();
	}
}
